import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, MapPin, Ship, Users, UserCheck,
  ClipboardList, Bell, MessageSquare, FileText,
  CalendarDays, RefreshCw, Settings, Award, Smartphone, ShieldCheck, BookOpen,
  LogOut, LucideIcon, Lock, CheckCircle, AlertCircle, X, Layers,
  Moon, CalendarClock, Palmtree, Anchor, AlertTriangle, IdCard, LayoutGrid, Printer,
  History,
} from 'lucide-react';
import * as api from '../services/api';
import { post } from '../services/api';
import PasswordInput from './PasswordInput';
import { BRAND_NAME, BRAND_SUB } from '../utils/brand';
import { hasFeature } from '../utils/featureFlags';

interface MenuItem { path: string; icon: LucideIcon; label: string; badge?: number; }
interface MenuGroup { label?: string; items: MenuItem[]; }

// 管理サイトの各ページ → 必要な役職権限キー（役職ベースの制限付きアクセスの表示制御用）。
// 未掲載パス（ダッシュボード・マニュアル）は常に表示。'_adminOnly' は正規管理者のみ。
export const ADMIN_PATH_FEATURE: Record<string, string> = {
  '/attendance': 'admin_attendance', '/rest-hours': 'admin_attendance',
  '/monthly': 'admin_attendance', '/reports': 'admin_attendance',
  '/leave': 'admin_leave',
  '/alerts': 'admin_alerts',
  '/messages': 'admin_messages',
  '/incidents': 'admin_incidents',
  '/boarding': 'admin_crew', '/users': 'admin_crew', '/talent': 'admin_crew',
  '/crew-sim': 'admin_crew', '/crew-assign': 'admin_crew', '/crew-deployment': 'admin_crew', '/crew-certificates': 'admin_crew',
  '/vessels': 'admin_vessels', '/gps': 'admin_vessels',
  '/certificates': 'admin_documents', '/documents': 'admin_documents',
  '/devices': 'admin_devices',
  '/master/activities': '_adminOnly', '/sync-logs': '_adminOnly', '/settings': '_adminOnly', '/position-permissions': '_adminOnly',
  '/audit-logs': '_adminOnly',
};

function PasswordModal({ onClose }: { onClose: () => void }) {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw]         = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving]       = useState(false);
  const [result, setResult]       = useState<{ ok: boolean; msg: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setResult({ ok: false, msg: '新しいパスワードが一致しません。' }); return; }
    if (newPw.length < 6)   { setResult({ ok: false, msg: 'パスワードは6文字以上にしてください。' }); return; }
    setSaving(true); setResult(null);
    try {
      await post('/users/me/change-password', { current_password: currentPw, new_password: newPw });
      setResult({ ok: true, msg: 'パスワードを変更しました。' });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err: any) {
      setResult({ ok: false, msg: err.message ?? '変更に失敗しました。' });
    } finally { setSaving(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 28, width: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <Lock size={16} color="#0066CC" />パスワード変更
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { label: '現在のパスワード', value: currentPw, set: setCurrentPw },
            { label: '新しいパスワード', value: newPw,     set: setNewPw },
            { label: '新しいパスワード（確認）', value: confirmPw, set: setConfirmPw },
          ].map(({ label, value, set }) => (
            <label key={label} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{label}</span>
              <PasswordInput value={value} onChange={e => set(e.target.value)} required
                style={{ padding: '9px 12px', border: '1.5px solid #D1D5DB', borderRadius: 8, fontSize: 14, outline: 'none' }}
                onFocus={e => e.target.style.borderColor = '#0066CC'}
                onBlur={e => e.target.style.borderColor = '#D1D5DB'}
              />
            </label>
          ))}
          {result && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7,
              padding: '9px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: result.ok ? '#F0FDF4' : '#FEF2F2',
              color: result.ok ? '#16A34A' : '#DC2626',
            }}>
              {result.ok ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
              {result.msg}
            </div>
          )}
          <button type="submit" disabled={saving} style={{
            padding: '10px 0', background: '#0066CC', color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          }}>
            {saving ? '変更中...' : '変更する'}
          </button>
        </form>
        <p style={{ marginTop: 10, fontSize: 11, color: '#9CA3AF', textAlign: 'center' }}>
          変更後も現在のセッションは継続されます
        </p>
      </div>
    </div>
  );
}

export const Sidebar: React.FC<{ onLogout: () => void; userName: string; isSuperAdmin?: boolean; userRole?: string }> = ({ onLogout, userName, isSuperAdmin, userRole }) => {
  const nav = useNavigate();
  const loc = useLocation();
  const [showPwModal, setShowPwModal] = useState(false);
  // 役職ベースの制限付き管理者は、許可された機能だけメニュー表示する
  const restricted = !!userRole && !['system_admin', 'shore_admin'].includes(userRole);
  const [allowedFeatures, setAllowedFeatures] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    if (!restricted) return;
    api.get<any>('/position-permissions/my').then(r => setAllowedFeatures(r?.data ?? {})).catch(() => setAllowedFeatures({}));
  }, [restricted]);
  const [alertCount, setAlertCount] = useState<{ critical: number; warning: number; total: number }>({ critical: 0, warning: 0, total: 0 });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 機能フラグはログイン直後に非同期で届くため、届いたら再描画してメニューに反映する
  const [, setFeatureTick] = useState(0);
  useEffect(() => {
    const bump = () => setFeatureTick(t => t + 1);
    window.addEventListener('catms-features-updated', bump);
    return () => window.removeEventListener('catms-features-updated', bump);
  }, []);

  const [restCount, setRestCount] = useState(0);

  const fetchAlertCount = async () => {
    if (!api.getToken()) return; // ログアウト済み・セッション切れのタブからは叩かない
    try {
      const res = await api.get<any>('/alerts/count');
      const d = res?.data ?? {};
      setAlertCount({ critical: d.critical ?? 0, warning: d.warning ?? 0, total: d.total ?? 0 });
    } catch { /* ignore */ }
    try {
      const res = await api.get<any>('/leave/rest-pending-count');
      setRestCount(res?.data?.count ?? 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchAlertCount();
    timerRef.current = setInterval(fetchAlertCount, 30_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  // デザイン設定をローカルに持ってきてサイドバーに反映
  const [designColor, setDesignColor] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_design') ?? '{}').primary_color ?? '#0066CC'; }
    catch { return '#0066CC'; }
  });
  const [designLogo, setDesignLogo] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_design') ?? '{}').logo_url ?? null; }
    catch { return null; }
  });

  useEffect(() => {
    const apply = () => {
      try {
        const d = JSON.parse(localStorage.getItem('admin_design') ?? '{}');
        if (d.primary_color) setDesignColor(d.primary_color);
        if (d.logo_url !== undefined) setDesignLogo(d.logo_url);
      } catch { /* ignore */ }
    };
    window.addEventListener('design-updated', apply);
    return () => window.removeEventListener('design-updated', apply);
  }, []);

  const menuGroups: MenuGroup[] = [
    { items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'ダッシュボード' },
    ]},
    // カツオポータル（機能フラグ portal の会社だけ表示。ポータル内は専用のナビを持つ）
    ...(hasFeature('portal') ? [{ label: 'ポータル', items: [
      { path: '/portal', icon: LayoutGrid, label: 'ポータルを開く' },
    ]}] : []),
    { label: '運航管理', items: [
      { path: '/gps',     icon: MapPin, label: 'GPS位置情報' },
      { path: '/vessels', icon: Ship,   label: '船舶管理' },
    ]},
    { label: '乗組員', items: [
      { path: '/users',       icon: Users,      label: 'ユーザー管理' },
      { path: '/talent',      icon: IdCard,     label: '人材管理' },
      { path: '/crew-sim',    icon: LayoutGrid, label: '配乗シミュレーション' },
      { path: '/crew-assign', icon: UserCheck,  label: '乗下船表' },
      // 資格・期限管理は会社ごとの機能フラグで出し分け（まずユニゾン・テクノロジーだけ）
      ...(hasFeature('crew_certificates') ? [{ path: '/crew-certificates', icon: Award, label: '資格・期限管理' }] : []),
    ]},
    { label: '勤怠・連絡', items: [
      { path: '/attendance', icon: ClipboardList, label: '勤怠一覧' },
      { path: '/leave',      icon: Palmtree,      label: '休暇管理', badge: restCount },
      { path: '/rest-hours', icon: Moon,          label: '休息時間' },
      { path: '/alerts',     icon: Bell,          label: 'アラート', badge: alertCount.total },
      { path: '/messages',   icon: MessageSquare, label: '連絡・お知らせ' },
      { path: '/documents',  icon: Printer,       label: '書類配信' },
      { path: '/reports',    icon: FileText,      label: 'レポート' },
      { path: '/monthly',    icon: CalendarDays,  label: '月次集計' },
    ]},
    { label: '労務・安全', items: [
      { path: '/boarding',     icon: Anchor,        label: '乗下船記録' },
      { path: '/incidents',    icon: AlertTriangle, label: 'ヒヤリハット' },
    ]},
    { label: 'システム', items: [
      { path: '/master/activities',    icon: Layers,    label: '登録マスタ' },
      { path: '/sync-logs',         icon: RefreshCw, label: '同期ログ' },
      { path: '/audit-logs',        icon: History,   label: '操作履歴' },
      { path: '/certificates',      icon: Award,     label: '証明書発行' },
      { path: '/devices',           icon: Smartphone, label: 'デバイス管理' },
      { path: '/manual',            icon: BookOpen,  label: '操作マニュアル' },
      { path: '/settings',          icon: Settings,  label: '設定' },
    ]},
    // 準備中の機能はここへまとめる（公開時に本来のグループへ戻す）
    { label: '制作中', items: [
      { path: '/crew-deployment', icon: CalendarClock, label: '配乗計画' },
    ]},
  ];

  return (
    <aside style={{
      width: 240, minWidth: 240, height: '100vh',
      background: '#FFFFFF',
      display: 'flex', flexDirection: 'column',
      borderRight: '1px solid #E5E7EB',
      flexShrink: 0, zIndex: 10,
    }}>

      {/* ロゴ */}
      <div style={{ padding: '18px 20px 16px', borderBottom: '1px solid #F3F4F6', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
            background: designLogo ? '#f8fafc' : `linear-gradient(135deg, ${designColor}, ${designColor}88)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 2px 6px ${designColor}44`, overflow: 'hidden',
            border: designLogo ? '1px solid #e5e7eb' : 'none',
          }}>
            {designLogo
              ? <img src={designLogo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              : <Ship size={18} color="#fff" strokeWidth={2.2} />
            }
          </div>
          <div>
            <div style={{ color: '#111827', fontSize: 15, fontWeight: 800, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              {BRAND_NAME}
            </div>
            <div style={{ color: '#9CA3AF', fontSize: 10, fontWeight: 500, letterSpacing: '0.06em', marginTop: 1 }}>
              {BRAND_SUB}
            </div>
          </div>
        </div>
        {isSuperAdmin && (
          <div style={{
            marginTop: 12, display: 'flex', alignItems: 'center', gap: 6,
            background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', color: '#fff',
            borderRadius: 8, padding: '6px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
          }}>
            <ShieldCheck size={13} strokeWidth={2.4} />
            運営管理者モード
          </div>
        )}
      </div>

      {/* ナビゲーション */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 10px 4px' }}>
        {(() => {
          if (!restricted) return menuGroups;
          const visible = (path: string): boolean => {
            const f = ADMIN_PATH_FEATURE[path];
            if (!f) return true;                 // ダッシュボード・マニュアル等
            if (f === '_adminOnly') return false; // 正規管理者専用ツール
            return !!allowedFeatures?.[f];
          };
          return menuGroups
            .map(g => ({ ...g, items: g.items.filter(it => visible(it.path)) }))
            .filter(g => g.items.length > 0);
        })().map((group, gi) => (
          <div key={gi} style={{ marginBottom: 4 }}>
            {group.label && (
              <div style={{
                padding: '10px 10px 4px',
                fontSize: 11, fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: '#9CA3AF',
                userSelect: 'none',
              }}>
                {group.label}
              </div>
            )}
            {group.items.map(m => {
              // /portal はポータルホームだけに一致させる（/portal/board 等で二重に光らないように）
              const isActive = m.path === '/portal' ? loc.pathname === '/portal' : loc.pathname.startsWith(m.path);
              const Icon = m.icon;
              const hasBadge = (m.badge ?? 0) > 0;
              const isCritical = m.path === '/alerts' && alertCount.critical > 0;

              return (
                <button
                  key={m.path}
                  onClick={() => nav(m.path)}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', gap: 10,
                    minHeight: 42, padding: '0 12px',
                    borderRadius: 8,
                    border: 'none',
                    background: isActive ? `${designColor}14` : 'transparent',
                    color: isActive ? designColor : '#374151',
                    fontSize: 14, fontWeight: isActive ? 600 : 400,
                    cursor: 'pointer', textAlign: 'left',
                    marginBottom: 1,
                    transition: 'background 0.1s, color 0.1s',
                    borderLeft: isActive ? `3px solid ${designColor}` : '3px solid transparent',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = '#F9FAFB';
                      e.currentTarget.style.color = '#111827';
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#374151';
                    }
                  }}
                >
                  <span style={{
                    width: 20, height: 20,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    color: isActive ? designColor : '#6B7280',
                    position: 'relative',
                  }}>
                    <Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
                    {/* critical アラートがある場合はアイコンに赤い点を表示 */}
                    {isCritical && (
                      <span style={{
                        position: 'absolute', top: -2, right: -2,
                        width: 7, height: 7, borderRadius: '50%',
                        background: '#DC2626', border: '1.5px solid #fff',
                      }} />
                    )}
                  </span>

                  <span style={{ flex: 1 }}>{m.label}</span>

                  {/* バッジ（未解決件数） */}
                  {hasBadge && (
                    <span style={{
                      minWidth: 18, height: 18, borderRadius: 9,
                      background: isCritical ? '#DC2626' : '#D97706',
                      color: '#fff',
                      fontSize: 10, fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 5px', flexShrink: 0,
                      lineHeight: 1,
                    }}>
                      {(m.badge ?? 0) > 99 ? '99+' : m.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* ユーザーフッター */}
      <div style={{ padding: '10px 10px 14px', borderTop: '1px solid #F3F4F6', flexShrink: 0 }}>
        {/* ユーザー情報 → 設定ページへ */}
        <button onClick={() => nav('/settings')} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px', marginBottom: 6,
          border: 'none', borderRadius: 8, background: 'transparent',
          cursor: 'pointer', textAlign: 'left',
          transition: 'background 0.1s',
        }}
          onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          <div style={{
            width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, #0066CC, #38BDF8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 800, color: '#fff',
          }}>
            {userName.replace(/\s/g, '').slice(0, 2)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userName}</div>
            <div style={{ fontSize: 11, color: '#9CA3AF' }}>設定を開く</div>
          </div>
          <Settings size={13} color="#9CA3AF" />
        </button>

        {/* ログアウト */}
        <button onClick={onLogout} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          minHeight: 38, padding: '0 12px',
          borderRadius: 8, border: '1px solid #E5E7EB',
          background: '#fff', color: '#6B7280', fontSize: 13, fontWeight: 500,
          cursor: 'pointer', textAlign: 'left',
          transition: 'background 0.1s, color 0.1s, border-color 0.1s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = '#FEF2F2'; e.currentTarget.style.color = '#DC2626'; e.currentTarget.style.borderColor = '#FECACA'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#6B7280'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
        >
          <LogOut size={15} strokeWidth={1.8} />
          <span>ログアウト</span>
        </button>
      </div>

      {showPwModal && <PasswordModal onClose={() => setShowPwModal(false)} />}
    </aside>
  );
};

export default Sidebar;
